import moment from 'moment';
import { sortBy } from 'lodash';
import ReactDOMServer from 'react-dom/server';
import { GlobalToken } from 'antd';

import { LegendLabel } from '@/comp/Trends/LegendLabel';
import { getTrendsChartColors } from '@/theme';
import { SPECIFIC_POWER_THRESHOLD } from '@/store/Station/StationStore.utils';

export const getInitOptions = (token: GlobalToken) => ({
  chart: {
    backgroundColor: token.colorBgContainer,
    type: 'line',
    zoomType: 'x',
    panning: {
      enabled: true,
    },
    panKey: 'alt',
    marginLeft: 50,
    style: {
      fontFamily: 'Heebo',
    },
  },
  boost: {},
  series: [],
  plotOptions: {
    line: {
      point: {
        events: {
          mouseOver: function () {
            const tickskArr = Object.values(this.series.xAxis.ticks);
            const tickskArrSorted = sortBy(tickskArr, ['pos']);
            this.selectedTick = tickskArrSorted.reverse().find((tick: { pos: number }) => tick.pos <= this.x);

            if (!this.selectedTick) return;

            this.selectedTick.label.css({
              color: token.colorPrimary,
            });
          },
          mouseOut: function () {
            if (this.selectedTick && this.selectedTick.label) {
              this.selectedTick.label.css({
                color: token.colorTextSecondary,
              });
            }
            this.selectedTick = null;
          },
        },
      },
      marker: {
        symbol: 'circle',
        enabled: false,
        states: {
          hover: {
            enabled: true,
            fillColor: token.colorBgContainer,
            lineWidth: 5,
            radius: 6,
          },
        },
      },
    },
  },
  legend: {
    enabled: true,
    align: 'left',
    verticalAlign: 'top',
    itemMarginBottom: 75,
    x: -10,
    y: 0,
    labelFormatter() {
      return ReactDOMServer.renderToString(<LegendLabel name={this.name} token={token} />);
    },
    useHTML: true,
    itemStyle: {
      color: token.colorTextSecondary,
    },
    itemHoverStyle: {
      color: token.colorText,
    },
    itemHiddenStyle: {
      color: token.colorBorder,
    },
  },
  title: {
    text: null,
  },
  credits: {
    enabled: false,
  },
  time: {
    useUTC: false,
  },
  noData: {
    style: {
      fontSize: 16,
      fontWeight: 500,
      color: token.colorText,
      fontFamily: 'Heebo, sans-serif',
    },
  },
  colors: getTrendsChartColors(token),
});

export const getRanges = t => ({
  LAST_24_HOURS: {
    key: 'LAST_24_HOURS',
    title: t('Last 24 hours'),
    get startDate() {
      return moment().subtract(24, 'hour').startOf('minute').utc().format();
    },
    get endDate() {
      return moment().subtract(1, 'minute').endOf('minute').utc().format();
    },
  },
  YESTERDAY: {
    key: 'YESTERDAY',
    title: t('Yesterday'),
    get startDate() {
      return moment().subtract(1, 'day').startOf('day').utc().format();
    },
    get endDate() {
      return moment().subtract(1, 'day').endOf('day').utc().format();
    },
  },
  LAST_7_DAYS: {
    key: 'LAST_7_DAYS',
    title: t('Last 7 days'),
    get startDate() {
      return moment().subtract(7, 'day').startOf('day').utc().format();
    },
    get endDate() {
      return moment().subtract(1, 'day').endOf('day').utc().format();
    },
  },
  LAST_30_DAYS: {
    key: 'LAST_30_DAYS',
    title: t('Last 30 days'),
    get startDate() {
      return moment().subtract(30, 'day').startOf('day').utc().format();
    },
    get endDate() {
      return moment().subtract(1, 'day').endOf('day').utc().format();
    },
  },
  LAST_90_DAYS: {
    key: 'LAST_90_DAYS',
    title: t('Last 90 days'),
    get startDate() {
      return moment().subtract(90, 'day').startOf('day').utc().format();
    },
    get endDate() {
      return moment().subtract(1, 'day').endOf('day').utc().format();
    },
  },
});

export const getAverage = (point1, point2, t) => unit => ({
  value: point2.diff(point1, unit),
  unit: t(unit),
});

export const getResolutionLabel = (points, t) => {
  const point1 = moment(points[0]?.series?.xData[0]);
  const point2 = moment(points[0]?.series?.xData[1]);
  const duration = +point2 - +point1;
  const getAverageByMeasure = getAverage(point1, point2, t);

  let average;
  if (duration < 1000 * 60) average = getAverageByMeasure('second');
  else if (duration < 1000 * 60 * 60) average = getAverageByMeasure('minute');
  else if (duration < 1000 * 60 * 60 * 24) average = getAverageByMeasure('hour');
  else if (duration < 1000 * 60 * 60 * 24 * 7) average = getAverageByMeasure('day');
  else if (duration < 1000 * 60 * 60 * 24 * 7 * 30) average = getAverageByMeasure('week');
  else if (duration < 1000 * 60 * 60 * 24 * 7 * 30 * 360) average = getAverageByMeasure('month');

  return t('{{value}} {{unit}} average', {
    value: average?.value,
    unit: average?.unit,
  });
};

export const convertData = (tvalue, data, from) => data.map(item => [item[0], tvalue(item[1], from)]);

export const convertSpecificPowerData = (tvalue, data, from) =>
  data.map(item => [item[0], tvalue(item[1], from) >= SPECIFIC_POWER_THRESHOLD ? null : tvalue(item[1], from)]);
